import { Auth } from './modules/auth.js'
import { Search } from './modules/search.js'
import { Notification } from './modules/notification.js'
import { Captcha } from './modules/captcha.js'
import { NAV_LINKS } from './utils/constants.js'
import { setCookie, getCookie } from './utils/cookie.js'

/**
 * GamerskyNav 主类
 * 严格按照 gamersky-nav-nuxt 组件实现
 */
export default class GamerskyNav {
  constructor(options = {}) {
    this.options = {
      mode: options.mode || 'default',
      showThemeSwitch: options.showThemeSwitch !== false,
      showSearch: options.showSearch !== false,
      onLoginSuccess: options.onLoginSuccess || (() => {}),
      onLogout: options.onLogout || (() => {}),
      onSearch: options.onSearch || (() => {}),
      onThemeChange: options.onThemeChange || (() => {}),
      onNotification: options.onNotification || (() => {}),
      onReady: options.onReady || (() => {})
    }

    this.container = null
    this.user = null
    this.isUserReady = false
    this.showAppMenu = false
    this.showLogin = false
    this.loginMode = 'sms'
    this.phone = ''
    this.smsCode = ''
    this.username = ''
    this.password = ''
    this.rememberMe = true
    this.countdown = 0
    this.loginEl = null
    this.themeAnimations = []

    this.auth = new Auth({
      onLoginSuccess: (user) => this.handleLoginSuccess(user),
      onLogout: () => this.onLogoutCallback()
    })

    this.search = new Search({
      placeholder: '',
      onSearch: (data) => this.options.onSearch(data)
    })

    this.notification = new Notification({
      onNotification: (data) => this.handleNotification(data)
    })

    this.captcha = new Captcha({
      onSuccess: () => this.handleCaptchaSuccess(),
      onError: (msg) => this.handleCaptchaError(msg)
    })

    this.init()
  }

  async init() {
    await this.auth.initUser()
    this.user = this.auth.user
    this.isUserReady = true
    if (this.container) {
      this.render()
    }

    // 初始化主题
    const theme = getCookie('Cookieheibai') || 'bai'
    document.body.classList.add(theme)

    this.options.onReady({
      user: this.user,
      isLoggedIn: !!this.user
    })
  }

  handleLoginSuccess(user) {
    this.user = user
    this.isUserReady = true
    this.options.onLoginSuccess(user)
    // 登录成功后刷新页面
    window.location.reload()
  }

  onLogoutCallback() {
    this.user = null
    this.isUserReady = true
    this.options.onLogout()
    this.render()
    this.notification.setUser(null)
  }

  handleNotification(data) {
    if (data.action === 'login') {
      this.showLoginDialog()
    }
    this.options.onNotification(data)
  }

  handleCaptchaSuccess() {
    this.countdown = 60
    const timer = setInterval(() => {
      this.countdown--
      if (this.countdown <= 0) {
        clearInterval(timer)
      }
      this.updateCountdownUI()
    }, 1000)
    this.updateCountdownUI()
  }

  handleCaptchaError(msg) {
    alert(msg || '验证码获取失败')
  }

  showLoginDialog() {
    this.showLogin = true
    if (this.loginEl) {
      this.loginEl.style.display = 'block'
    }
  }

  /**
   * 检测登录状态，未登录则调起登录弹窗，已登录则执行 callback
   * @param {function} callback - 登录状态下执行的回调，接收 user 对象参数
   */
  UserOnline(callback) {
    if (typeof callback !== 'function') return

    if (!this.user) {
      this.auth.initUser().then(() => {
        this.user = this.auth.user
        this.isUserReady = true
        if (this.user) {
          callback(this.user)
        } else {
          this.showLoginDialog()
        }
      })
    } else {
      callback(this.user)
    }
  }

  hideLoginDialog() {
    this.showLogin = false
    if (this.loginEl) {
      this.loginEl.style.display = 'none'
    }
    this.phone = ''
    this.smsCode = ''
    this.username = ''
    this.password = ''
    this.countdown = 0
  }

  toggleTheme() {
    const currentTheme = getCookie('Cookieheibai') || 'bai'
    const newTheme = currentTheme === 'bai' ? 'hei' : 'bai'

    setCookie('Cookieheibai', newTheme)
    document.body.classList.remove(currentTheme)
    document.body.classList.add(newTheme)
    this.animateThemeSwitch(currentTheme, newTheme)

    this.options.onThemeChange(newTheme)
  }

  animateThemeSwitch(currentTheme, newTheme) {
    if (!this.container || window.matchMedia?.('(prefers-reduced-motion: reduce)').matches) return

    const themeEl = this.container.querySelector('.topbar2 .QZ-links .theme')
    if (!themeEl || typeof themeEl.animate !== 'function') return

    this.themeAnimations.forEach((animation) => animation.cancel())
    this.themeAnimations = []

    const duration = 260
    const fastDuration = 190
    const easing = 'cubic-bezier(0.25, 1, 0.5, 1)'
    const fromX = currentTheme === 'hei' ? 18 : 0
    const toX = newTheme === 'hei' ? 18 : 0
    const isNight = newTheme === 'hei'

    const run = (el, frames, options = {}) => {
      if (!el || typeof el.animate !== 'function') return
      const animation = el.animate(frames, {
        duration,
        easing,
        ...options
      })
      this.themeAnimations.push(animation)
      animation.addEventListener('finish', () => {
        this.themeAnimations = this.themeAnimations.filter((item) => item !== animation)
      })
    }

    run(themeEl.querySelector('.theme-circle'), [
      { transform: `translateX(${fromX}px) scale(0.92)` },
      { transform: `translateX(${toX}px) scale(1)` }
    ])
    run(themeEl.querySelector('.theme-rect-day'), [
      { opacity: isNight ? 1 : 0 },
      { opacity: isNight ? 0 : 1 }
    ])
    run(themeEl.querySelector('.theme-rect-night'), [
      { opacity: isNight ? 0 : 1 },
      { opacity: isNight ? 1 : 0 }
    ])
    run(themeEl.querySelector('.theme-sun'), [
      { opacity: isNight ? 0.75 : 0 },
      { opacity: isNight ? 0 : 0.75 }
    ], { duration: fastDuration })
    run(themeEl.querySelector('.theme-moon'), [
      { opacity: isNight ? 0 : 0.75 },
      { opacity: isNight ? 0.75 : 0 }
    ], { duration: fastDuration })
  }

  updateCountdownUI() {
    if (!this.loginEl) return
    const codeBtn = this.loginEl.querySelector('.code-btn')
    if (codeBtn) {
      codeBtn.disabled = this.countdown > 0
      codeBtn.textContent = this.countdown > 0 ? `${this.countdown}s后重新获取` : '获取验证码'
      codeBtn.style.color = this.countdown > 0 ? '#999' : '#1481c2'
    }
  }

  async handlePasswordLogin() {
    if (!this.username) {
      alert('请填写用户名！')
      return
    }
    if (!this.password) {
      alert('请填写密码！')
      return
    }

    const result = await this.auth.loginWithPassword(this.username, this.password, this.rememberMe)

    if (result.success) {
      this.hideLoginDialog()
    } else {
      alert(result.message)
    }
  }

  async handleSmsLogin() {
    if (!this.phone) {
      alert('请输入手机号码')
      return
    }
    if (!this.smsCode) {
      alert('请输入验证码')
      return
    }

    const result = await this.auth.loginWithSms(this.phone, this.smsCode)

    if (result.success) {
      this.hideLoginDialog()
    } else {
      alert(result.message)
    }
  }

  async handleLogout() {
    const result = await this.auth.logout()
    if (result.success) {
      this.options.onLogout()
      window.location.reload()
    } else {
      alert('退出登录失败，请重试')
    }
  }

  oauthLogin(type) {
    this.auth.oauthLogin(type, window.location.href)
  }

  async showCaptcha() {
    if (!this.phone) {
      alert('请输入手机号码')
      return
    }

    const phoneReg = /^1[345789]\d{9}$/
    if (!phoneReg.test(this.phone)) {
      alert('手机号格式不正确！')
      return
    }

    await this.captcha.show(this.phone)
  }

  /**
   * 渲染登录弹窗 - 严格按照 UserLogin.vue 模板
   */
  renderLoginBox() {
    return `
      <div class="QZshade" style="display: none;"></div>
      <div class="QZlogin" style="display: none;">
        <a class="QZ-close" href="javascript:;">×</a>
        <div class="QZ-button">
          <span class="${this.loginMode === 'sms' ? 'QZ-active' : ''}" data-mode="sms">短信登录</span>
          <span class="${this.loginMode === 'password' ? 'QZ-active' : ''}" data-mode="password">密码登录</span>
        </div>
        <div class="sms-login" style="display: ${this.loginMode === 'sms' ? 'block' : 'none'};">
          <div class="QZ-input inp-phone">
            <input type="text" maxlength="35" placeholder="请输入手机号" class="phone-input">
          </div>
          <div class="QZ-input code">
            <input type="text" maxlength="6" placeholder="请输入验证码" class="sms-code-input">
            <button class="code-btn">获取验证码</button>
          </div>
          <div class="QZ-btn">
            <a id="QZsmsbtn" href="javascript:;">登 录 / 注 册</a>
          </div>
          <div class="QZ-chk">
            <label>
              <input type="checkbox" class="remember-checkbox" checked>
              下次自动登录
            </label>
          </div>
          <div class="QZ-chk">
            <span>登录即代表同意<a href="//i.gamersky.com/user/agreement/" target="_blank">《游民星空服务条款与隐私条款》</a></span>
          </div>
        </div>
        <div class="password-login" style="display: ${this.loginMode === 'password' ? 'block' : 'none'};">
          <div class="QZ-input">
            <input type="text" maxlength="35" placeholder="请输入手机号/邮箱/用户名" class="username-input">
          </div>
          <div class="QZ-input code">
            <input type="password" maxlength="35" placeholder="请输入密码" class="password-input">
            <a href="https://i.gamersky.com/user/getpassword" target="_blank" class="QZ-link forgetPaw">忘记密码</a>
          </div>
          <div class="QZ-btn">
            <a id="QZbtn" href="javascript:;">登 录</a>
          </div>
          <div class="QZ-chk">
            <label>
              <input type="checkbox" class="remember-checkbox" checked>
              下次自动登录
            </label>
            <a class="QZ-reg" href="https://i.gamersky.com/user/register" target="_blank">立即注册</a>
          </div>
          <div class="QZ-chk">
            <span>登录即代表同意<a href="//i.gamersky.com/user/agreement/" target="_blank">《游民星空服务条款与隐私条款》</a></span>
          </div>
        </div>
        <fieldset><legend></legend></fieldset>
        <div class="QZ-con">
          <a href="javascript:;" id="QZweixinLogin"></a>
          <a href="javascript:;" id="QZqqLogin"></a>
        </div>
      </div>
    `
  }

  /**
   * 渲染主题切换 SVG - 严格按照 NavBar.vue 中的 SVG
   */
  renderThemeSwitch() {
    if (!this.options.showThemeSwitch || this.options.mode !== 'default') return ''
    return `
      <svg class="theme" id="switch" fill="none" viewBox="0 0 40 22" style="transition: 0.3s">
        <defs>
          <linearGradient gradientUnits="userSpaceOnUse" id="dayGradient" x1="39.9798" x2="39.9798" y1="21.9778" y2="0">
            <stop stop-color="#FF6000"></stop>
            <stop offset="1" stop-color="#E22200"></stop>
          </linearGradient>
          <linearGradient gradientUnits="userSpaceOnUse" id="nightGradient" x1="39.9798" x2="39.9798" y1="21.9778" y2="0">
            <stop stop-color="#00BFFF"></stop>
            <stop offset="1" stop-color="#004BE2"></stop>
          </linearGradient>
          <linearGradient gradientUnits="userSpaceOnUse" id="handleGradient" x1="2" x2="2" y1="2" y2="20">
            <stop stop-color="white" stop-opacity="0.75"></stop>
            <stop offset="1" stop-color="white"></stop>
          </linearGradient>
          <filter color-interpolation-filters="sRGB" filterUnits="userSpaceOnUse" height="22" id="handleShadow" width="22" x="0" y="1">
            <feFlood flood-opacity="0" result="BackgroundImageFix"></feFlood>
            <feColorMatrix in="SourceAlpha" result="hardAlpha" type="matrix" values="0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 127 0"></feColorMatrix>
            <feOffset dy="1"></feOffset>
            <feGaussianBlur stdDeviation="1"></feGaussianBlur>
            <feComposite in2="hardAlpha" operator="out"></feComposite>
            <feColorMatrix type="matrix" values="0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0.3 0"></feColorMatrix>
            <feBlend in2="BackgroundImageFix" mode="normal" result="effect1_dropShadow"></feBlend>
            <feBlend in="SourceGraphic" in2="effect1_dropShadow" mode="normal" result="shape"></feBlend>
          </filter>
          <clipPath id="sunClip">
            <rect fill="white" height="12" transform="translate(23 5)" width="12"></rect>
          </clipPath>
          <clipPath id="moonClip">
            <rect fill="white" height="12" transform="translate(5 5)" width="12"></rect>
          </clipPath>
        </defs>
        <rect class="theme-rect theme-rect-day" fill="url(#dayGradient)" height="22" rx="11" width="40"></rect>
        <rect class="theme-rect theme-rect-night" fill="url(#nightGradient)" height="22" rx="11" width="40"></rect>
        <rect height="21" rx="10.5" stroke="white" stroke-opacity="0.2" width="39" x="0.5" y="0.5" fill="none"></rect>
        <g class="theme-circle">
          <circle cx="11" cy="11" fill="url(#handleGradient)" r="9" filter="url(#handleShadow)"></circle>
        </g>
        <g class="theme-sun" clip-path="url(#sunClip)">
          <path d="M29 15.5C29.3451 15.5 29.625 15.7799 29.625 16.125C29.625 16.4701 29.3451 16.75 29 16.75C28.6549 16.75 28.375 16.4701 28.375 16.125C28.375 15.7799 28.6549 15.5 29 15.5ZM25.375 14C25.7202 14 26 14.2799 26 14.625C26 14.9701 25.7202 15.25 25.375 15.25C25.0298 15.25 24.75 14.9701 24.75 14.625C24.75 14.2799 25.0298 14 25.375 14ZM32.625 14C32.9701 14 33.25 14.2799 33.25 14.625C33.25 14.9701 32.9701 15.25 32.625 15.25C32.2799 15.25 32 14.9701 32 14.625C32 14.2799 32.2799 14 32.625 14ZM29 7.25C31.071 7.25 32.75 8.92894 32.75 11C32.75 13.071 31.071 14.75 29 14.75C26.9289 14.75 25.25 13.071 25.25 11C25.25 8.92894 26.9289 7.25 29 7.25ZM29 8.25C27.4812 8.25 26.25 9.48122 26.25 11C26.25 12.5188 27.4812 13.75 29 13.75C30.5188 13.75 31.75 12.5188 31.75 11C31.75 9.48122 30.5188 8.25 29 8.25ZM23.875 10.375C24.2202 10.375 24.5 10.6549 24.5 11C24.5 11.3451 24.2202 11.625 23.875 11.625C23.5298 11.625 23.25 11.3451 23.25 11C23.25 10.6549 23.5298 10.375 23.875 10.375ZM34.125 10.375C34.4701 10.375 34.75 10.6549 34.75 11C34.75 11.3451 34.4701 11.625 34.125 11.625C33.7799 11.625 33.5 11.3451 33.5 11C33.5 10.6549 33.7799 10.375 34.125 10.375ZM25.375 6.75C25.7202 6.75 26 7.02982 26 7.375C26 7.72017 25.7202 8 25.375 8C25.0298 8 24.75 7.72017 24.75 7.375C24.75 7.02982 25.0298 6.75 25.375 6.75ZM32.625 6.75C32.9701 6.75 33.25 7.02982 33.25 7.375C33.25 7.72018 32.9701 8 32.625 8C32.2799 8 32 7.72018 32 7.375C32 7.02982 32.2799 6.75 32.625 6.75ZM29 5.25C29.3451 5.25 29.625 5.52982 29.625 5.875C29.625 6.22018 29.3451 6.5 29 6.5C28.6549 6.5 28.375 6.22018 28.375 5.875C28.375 5.52982 28.6549 5.25 29 5.25Z" fill="white" fill-opacity="0.75"></path>
        </g>
        <g class="theme-moon" clip-path="url(#moonClip)">
          <path d="M11 5.5C11.3812 5.5 11.7541 5.53915 12.1143 5.61328C12.3417 5.66026 12.5066 5.85763 12.5127 6.08984C12.5187 6.32204 12.3644 6.52821 12.1396 6.58691C10.987 6.88743 10.1368 7.93568 10.1367 9.18164C10.1367 10.6628 11.3373 11.8633 12.8184 11.8633C14.0644 11.8632 15.1126 11.013 15.4131 9.86035C15.4718 9.63562 15.6779 9.48133 15.9102 9.4873C16.1423 9.49334 16.3398 9.65826 16.3867 9.88574C16.4609 10.2459 16.5 10.6188 16.5 11C16.5 14.0375 14.0375 16.5 11 16.5C7.96244 16.5 5.5 14.0375 5.5 11C5.5 7.96244 7.96244 5.5 11 5.5ZM10.2275 6.56641C8.11051 6.93283 6.5 8.77823 6.5 11C6.5 13.4853 8.51471 15.5 11 15.5C13.2218 15.5 15.0671 13.8895 15.4336 11.7725C14.7665 12.4459 13.8416 12.8632 12.8184 12.8633C10.785 12.8633 9.13672 11.2151 9.13672 9.18164C9.13677 8.15843 9.55408 7.23344 10.2275 6.56641Z" fill="white" fill-opacity="0.75"></path>
        </g>
      </svg>
    `
  }

  ensureCaptchaElement() {
    const captchaEl = this.captcha.element || this.captcha.render()
    if (captchaEl && !captchaEl.parentNode) {
      document.body.appendChild(captchaEl)
    }
  }

  render() {
    if (!this.container) return

    // 添加验证码弹窗到 body，重复渲染时复用同一个验证码节点。
    this.ensureCaptchaElement()

    // 严格按照 NavBar.vue 模板结构
    // 注意：容器本身就是 .gamersky-nav-wrapper
    this.container.innerHTML = `
      <div class="QZnav" id="QZnav">
        <div class="topbar">
          <!-- 左侧：Logo + 导航链接 -->
          <div class="topbar1">
            <div class="QZ-home">
              ${this.options.mode === 'game-library' ? `
                <a class="alik ymxk yxk" href="https://ku.gamersky.com/" target="_blank"></a>
              ` : this.options.mode === 'game-zone' ? `
                <a class="alik ymxk yxzq" href="https://www.gamersky.com/z/" target="_blank"></a>
              ` : `
                <a class="alik ymxk" href="https://www.gamersky.com/" target="_blank">
                  GAMERSKY<em>.COM</em>
                </a>
              `}
            </div>
          </div>

          <!-- 搜索框 -->
          <div class="search-slot"></div>

          <!-- 右侧：功能区 -->
          <div class="topbar2">
            <div class="QZ-links">
              ${this.renderThemeSwitch()}

              <!-- 客户端 -->
              <span class="navtt app-navtt">
                <p class="alik app"><i></i>客户端</p>
                <div class="topbar-menu app">
                  <div class="topbar-appul">
                    <div class="appul-title">
                      <a class="applogo" href="https://a.gamersky.com/index.html?3&3" target="_blank"></a>
                      <a class="appname" href="https://a.gamersky.com/index.html?3&3" target="_blank">下载游民星空App</a>
                    </div>
                    <div class="appul-downloadBtns">
                      <a href="https://at.umeng.com/WT15jC" tit="0jaObe" target="_blank" data-itemid="995690" class="countHit countHitSql android">
                        <img src="//image.gamersky.com/webimg13/www/newPc/icon_18_android_fill.svg" />
                        <span>Android 版</span>
                      </a>
                      <a href="https://at.umeng.com/mqO5zq" tit="D419PD" target="_blank" data-itemid="995690" class="countHit countHitSql ios">
                        <img src="//image.gamersky.com/webimg13/www/newPc/icon_18_apple_fill.svg" />
                        <span>iOS 版</span>
                      </a>
                    </div>
                    <div class="appul-code">
                      <div class="appPhone">
                        <img class="appPhoneFrame" src="//image.gamersky.com/webimg13/www/newPc/qznav-app-phone-frame.png" alt="" />
                        <span class="appPhoneScreen">
                          <img class="appPhoneScreenImg" src="//image.gamersky.com/webimg13/www/newPc/qznav-app-phone-screen.png" alt="" />
                          <img class="appPhoneLight" src="//image.gamersky.com/webimg13/www/newPc/qznav-app-phone-light.svg" alt="" />
                        </span>
                      </div>
                      <div class="appCode">
                        <img class="ymcode" src="//image.gamersky.com/webimg13/www/newPc/qznav-app-qrcode.png" />
                        <span><i>使用游民星空App，</i>随时随地获取最新游戏资讯攻略</span>
                      </div>
                    </div>
                  </div>
                </div>
              </span>

              <!-- 消息通知 -->
              <div class="notification-slot"></div>
            </div>

            <!-- 用户状态 -->
            <div class="user-panel-slot"></div>
          </div>
        </div>
      </div>
      ${this.renderLoginBox()}
    `

    // 插入搜索框
    if (this.options.showSearch) {
      const searchSlot = this.container.querySelector('.search-slot')
      if (searchSlot) {
        searchSlot.appendChild(this.search.render())
      }
    }

    // 插入通知组件
    const notificationSlot = this.container.querySelector('.notification-slot')
    if (notificationSlot) {
      notificationSlot.appendChild(this.notification.render(this.user))
    }

    // 插入用户面板 - 严格按照 UserPanel.vue 模板
    const userPanelSlot = this.container.querySelector('.user-panel-slot')
    if (userPanelSlot) {
      if (!this.isUserReady) {
        userPanelSlot.innerHTML = ''
      } else if (!this.user) {
        userPanelSlot.innerHTML = `
          <div class="noLogin" style="display: flex">
            <a class="alik Login" href="javascript:;">登录</a>
            <a class="alik Reg" href="https://i.gamersky.com/user/register" target="_blank">注册</a>
          </div>
        `
      } else {
        userPanelSlot.innerHTML = `
          <div class="okLogin" style="display: block">
            <a class="alik uname" href="https://i.gamersky.com/home/" target="_blank">
              <img src="${this.user.userface}" alt="${this.user.username}" />
            </a>
            <div class="userLayer">
              <ul class="topbar-usercon">
                <li class="user1">
                  <div class="img">
                    <a href="http://i.gamersky.com/home/" target="_blank">
                      <img src="${this.user.userface}" />
                    </a>
                  </div>
                  <div class="info">
                    <div class="name">${this.user.username}</div>
                    <div class="binding">
                      <a href="https://i.gamersky.com/user/info/emailbind" class="email ${this.user.emailClass === 'ok' ? 'ok' : ''}" target="_blank" title="${this.user.emailClass === 'ok' ? '已绑定' : '点击绑定'}"></a>
                      <a href="https://i.gamersky.com/user/info/mobilebind" class="phone ${this.user.phoneClass === 'ok' ? 'ok' : ''}" target="_blank" title="${this.user.phoneClass === 'ok' ? '已绑定' : '点击绑定'}"></a>
                      <a href="https://i.gamersky.com/user/info/baseconnect/" class="weixin ${this.user.weixinClass === 'ok' ? 'ok' : ''}" target="_blank" title="${this.user.weixinClass === 'ok' ? '已绑定' : '点击绑定'}"></a>
                      <a href="https://i.gamersky.com/user/info/baseconnect/" class="qq ${this.user.qqClass === 'ok' ? 'ok' : ''}" target="_blank" title="${this.user.qqClass === 'ok' ? '已绑定' : '点击绑定'}"></a>
                    </div>
                  </div>
                </li>
                <li class="user2">
                  <a class="jb" href="https://i.gamersky.com/store" target="_blank">
                    <i></i>
                    <p>金币商城</p>
                  </a>
                  <a class="zl" href="https://i.gamersky.com/author" target="_blank">
                    <i></i>
                    <p>创作中心</p>
                  </a>
                </li>
                <hr>
                <li class="user3">
                  <div class="link">
                    <a href="https://i.gamersky.com/user/info/modifyinfo" target="_blank">修改资料</a>
                    |
                    <a href="https://i.gamersky.com/user/info/password" target="_blank">修改密码</a>
                  </div>
                  <div class="ExitSign">
                    <a href="javascript:;">退出登录</a>
                  </div>
                </li>
              </ul>
            </div>
          </div>
        `
      }
    }

    this.loginEl = this.container.querySelector('.QZlogin')
    this.bindEvents()
  }

  bindEvents() {
    if (!this.container) return

    const self = this

    // ========== 客户端 hover ==========
    const appNavtt = this.container.querySelector('.app-navtt')
    if (appNavtt) {
      appNavtt.addEventListener('mouseenter', () => appNavtt.classList.add('cur'))
      appNavtt.addEventListener('mouseleave', () => appNavtt.classList.remove('cur'))
    }

    // ========== 通知 hover ==========
    const notificationNavtt = this.container.querySelector('.notification-slot .navtt')
    if (notificationNavtt) {
      notificationNavtt.addEventListener('mouseenter', () => notificationNavtt.classList.add('cur'))
      notificationNavtt.addEventListener('mouseleave', () => notificationNavtt.classList.remove('cur'))
    }

    // ========== 用户面板 hover ==========
    const okLogin = this.container.querySelector('.okLogin')
    if (okLogin) {
      okLogin.addEventListener('mouseenter', () => okLogin.classList.add('cur'))
      okLogin.addEventListener('mouseleave', () => okLogin.classList.remove('cur'))
    }

    // ========== 主题切换 ==========
    const themeBtn = this.container.querySelector('.theme')
    if (themeBtn) {
      themeBtn.addEventListener('click', () => self.toggleTheme())
    }

    // ========== 登录按钮 ==========
    const loginBtn = this.container.querySelector('.noLogin .Login')
    if (loginBtn) {
      loginBtn.addEventListener('click', () => self.showLoginDialog())
    }

    // ========== 登录框关闭 ==========
    const closeBtn = this.container.querySelector('.QZ-close')
    const shade = this.container.querySelector('.QZshade')
    if (closeBtn) {
      closeBtn.addEventListener('click', () => self.hideLoginDialog())
    }
    if (shade) {
      shade.addEventListener('click', () => self.hideLoginDialog())
    }

    // ========== 登录模式切换 ==========
    const loginTabs = this.container.querySelectorAll('.QZ-button span')
    loginTabs.forEach(tab => {
      tab.addEventListener('click', () => {
        const mode = tab.dataset.mode
        self.loginMode = mode
        loginTabs.forEach(t => t.classList.remove('QZ-active'))
        tab.classList.add('QZ-active')

        const smsLogin = self.container.querySelector('.sms-login')
        const passwordLogin = self.container.querySelector('.password-login')
        if (smsLogin && passwordLogin) {
          smsLogin.style.display = mode === 'sms' ? 'block' : 'none'
          passwordLogin.style.display = mode === 'password' ? 'block' : 'none'
        }
      })
    })

    // ========== 输入框 ==========
    const phoneInput = this.container.querySelector('.phone-input')
    const smsCodeInput = this.container.querySelector('.sms-code-input')
    const usernameInput = this.container.querySelector('.username-input')
    const passwordInput = this.container.querySelector('.password-input')
    const rememberCheckboxes = this.container.querySelectorAll('.remember-checkbox')

    if (phoneInput) {
      phoneInput.addEventListener('input', (e) => { self.phone = e.target.value })
    }
    if (smsCodeInput) {
      smsCodeInput.addEventListener('input', (e) => { self.smsCode = e.target.value })
    }
    if (usernameInput) {
      usernameInput.addEventListener('input', (e) => { self.username = e.target.value })
    }
    if (passwordInput) {
      passwordInput.addEventListener('input', (e) => { self.password = e.target.value })
    }
    rememberCheckboxes.forEach(cb => {
      cb.addEventListener('change', (e) => { self.rememberMe = e.target.checked })
    })

    // ========== 获取验证码 ==========
    const codeBtn = this.container.querySelector('.code-btn')
    if (codeBtn) {
      codeBtn.addEventListener('click', () => self.showCaptcha())
    }

    // ========== 短信登录 ==========
    const smsBtn = this.container.querySelector('#QZsmsbtn')
    if (smsBtn) {
      smsBtn.addEventListener('click', () => self.handleSmsLogin())
    }

    // ========== 密码登录 ==========
    const pwdBtn = this.container.querySelector('#QZbtn')
    if (pwdBtn) {
      pwdBtn.addEventListener('click', () => self.handlePasswordLogin())
    }

    // ========== 退出登录 ==========
    const logoutBtn = this.container.querySelector('.ExitSign a')
    if (logoutBtn) {
      logoutBtn.addEventListener('click', async () => {
        await self.handleLogout()
      })
    }

    // ========== OAuth 登录 ==========
    const weixinLogin = this.container.querySelector('#QZweixinLogin')
    const qqLogin = this.container.querySelector('#QZqqLogin')
    if (weixinLogin) {
      weixinLogin.addEventListener('click', () => self.oauthLogin('weixin'))
    }
    if (qqLogin) {
      qqLogin.addEventListener('click', () => self.oauthLogin('qq'))
    }
  }

  mount(selector) {
    const el = typeof selector === 'string' ? document.querySelector(selector) : selector
    if (!el) {
      console.error('[GamerskyNav] 未找到挂载点:', selector)
      return
    }

    this.container = el
    this.render()
  }

  destroy() {
    if (this.container) {
      this.container.innerHTML = ''
    }
    this.notification.disconnectMqtt()
  }
}
